import React, { Fragment, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';

import EditIcon from '@mui/icons-material/EditOutlined';
import MoreIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';

import Table from '../shared/table';

import './procedures.scss';

const ProceduresTable = ({
  procedures = [],
  onEditClick = () => {},
  onDeleteClick = () => {},
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  totalRows,
  order,
  setOrder,
  orderBy,
  setOrderBy,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const columns = useMemo(() => {
    return [
      {
        title: 'Name',
        field: 'name',
        type: 'custom',
        sort: true,
        formatter: (value) => <span style={{ fontWeight: 'bold' }}>{value}</span>
      },
      {
        title: 'Description',
        field: 'description',
        sort: true,
      },
      {
        title: 'Flow',
        field: 'flow',
        sort: true,
      },
      {
        title: 'Products',
        field: 'products',
        type: 'custom',
        sort: false,
        formatter: (value, row) => (
          <Chip
            size="small"
            label={`${value?.length || 0} product${value?.length !== 1 ? 's' : ''}`}
            variant="outlined"
            sx={{ fontWeight: 500, backgroundColor: 'var(--color-main-light)', borderColor: 'var(--color-main-border)', mr: 1 }}
          />
        )
      },
      {
        title: 'Documents',
        field: 'documents',
        type: 'custom',
        sort: false,
        formatter: (value, row) => (
          <Chip
            size="small"
            label={`${value?.length || 0} document${value?.length !== 1 ? 's' : ''}`}
            variant="outlined"
            sx={{ fontWeight: 500, backgroundColor: 'var(--color-main-light)', borderColor: 'var(--color-main-border)', mr: 1 }}
          />
        )
      },
      {
        title: 'Countries',
        field: 'active',
        type: 'custom',
        sort: true,
        formatter: (value, row) => (
          (row?.countries || row?.active || [])?.map((country) => (
            <Chip
              key={country}
              size="small"
              label={country}
              variant="outlined"
              sx={{ fontWeight: 500, backgroundColor: 'var(--color-main-light)', borderColor: 'var(--color-main-border)', mr: 1 }}
            />
          ))
        ),
      },
      {
        title: '',
        field: 'id',
        type: 'custom',
        filter: false,
        width: 50,
        formatter: (value, row) => (
          <>
            <IconButton
              size="small"
              onClick={handleMenuOpen}
              aria-controls={`${row.id}_actions`}
              aria-haspopup="true"
              aria-label="actions"
              color="inherit"
              id={`${row.id}_actions`}
            >
              <MoreIcon color="secondary" />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              elevation={1}
              id={`${row.id}_actions`}
              keepMounted
              onClose={handleMenuClose}
              open={Boolean(anchorEl) && anchorEl.id === `${row.id}_actions`}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem
                onClick={() => {
                  onEditClick(row);
                  handleMenuClose();
                }}
              >
                <EditIcon fontSize="small" style={{ marginRight: 8 }} color="secondary" />
                Edit Procedure
              </MenuItem>
              <Divider />
              <MenuItem
                onClick={() => {
                  onDeleteClick(row?.id);
                  handleMenuClose();
                }}
              >
                <DeleteIcon fontSize="small" style={{ marginRight: 8, fill: '#F04438' }} />
                <Typography sx={{ color: '#F04438' }}>Delete Procedure</Typography>
              </MenuItem>
            </Menu>
          </>
        ),
      },
    ];
  }, [procedures, anchorEl]);

  return (
    <Fragment>
      <Table
        stickyTable
        rows={procedures?.map((procedure) => ({
          ...procedure,
          country: procedure?.country || '',
        }))}
        columns={columns}
        page={page}
        setPage={setPage}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        totalRows={totalRows}
        order={order}
        setOrder={setOrder}
        orderBy={orderBy}
        setOrderBy={setOrderBy}
      />
    </Fragment>
  );
};

ProceduresTable.propTypes = {
  procedures: PropTypes.array,
  distributors: PropTypes.array,
  onVerifyClick: PropTypes.func,
  onSMSClick: PropTypes.func
};

export default ProceduresTable;
